import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnInit, forwardRef } from '@angular/core';
import { ControlValueAccessor, FormControl, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { popInPopOut } from '@app/core/animations';
import { TranslateModule } from '@ngx-translate/core';
import { JwBootstrapSwitchNg2Module } from '@servoy/jw-bootstrap-switch-ng2';

export enum TristateSwitchValue {
  true = 1,
  false = 0,
  indeterminate = null as any,
}

@Component({
  selector: 'tristate-switch',
  standalone: true,
  imports: [CommonModule, FormsModule, JwBootstrapSwitchNg2Module, ReactiveFormsModule, TranslateModule],
  templateUrl: './tristate-switch.component.html',
  styleUrls: ['./tristate-switch.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TristateSwitchComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [popInPopOut],
})
export class TristateSwitchComponent implements ControlValueAccessor, OnInit {
  @Input() placeholder?: string;
  /**
   * Current form control.
   */
  protected formControl: FormControl = new FormControl(false);
  /**
   * Defines if the switch is disabled.
   */
  protected disabled = false;

  private currentValue: TristateSwitchValue = TristateSwitchValue.indeterminate;

  protected get isNullValue(): boolean {
    return this.currentValue === TristateSwitchValue.indeterminate;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onChange = (value: TristateSwitchValue) => {};

  onTouched = () => {};

  writeValue(val: TristateSwitchValue): void {
    this.currentValue = val ?? TristateSwitchValue.indeterminate;
    this.formControl.setValue(this.currentValue !== TristateSwitchValue.indeterminate ? this.currentValue : false, {
      emitEvent: false,
    });
  }

  ngOnInit(): void {
    this.formControl.valueChanges.subscribe((value) => {
      this.currentValue = value;
      this.onChange(value);
    });
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  protected handleClick($event: any): void {
    if (this.currentValue !== TristateSwitchValue.indeterminate) {
      return;
    }
    $event.preventDefault();
    $event.stopPropagation();
    this.formControl.setValue(true);
  }

  protected clear($event: any): void {
    $event.stopImmediatePropagation();
    $event.preventDefault();
    this.currentValue = TristateSwitchValue.indeterminate;
    this.formControl.setValue(false, { emitEvent: false });
    this.onChange(this.currentValue);
  }
}
